import type { MedplumClient } from '@medplum/core';
import type { EpisodeOfCare } from '@medplum/fhirtypes';
import { getServiceTypeCode } from '@unify/fhir-model/resources/episode-of-care';
import {
  codeFromServiceTypeCodeableConcept,
  displayLabelForEpisodeType,
} from '../../../lib/fhirValueSets/sessionAndEpisodeTypes';
import {
  loadPractitionerPreferredLabelMap,
  preferredLabelOrFallback,
} from '../../../lib/loadPractitionerPreferredLabels';

/** One selectable row per Episode of Care (not deduplicated by provider). */
export interface PortalEocOption {
  eocId: string;
  /** Practitioner id or fallback identifier */
  providerId: string;
  providerName: string;
  /** Live preferred label for new Practitioner Reference.display writes. */
  providerPreferredName?: string;
  episodeLabel: string;
  status: string;
}

export function practitionerIdFromRef(ref: string | undefined): string {
  if (!ref) return '';
  return ref.startsWith('Practitioner/') ? ref.slice('Practitioner/'.length) : ref;
}

export function eocDisplayName(eoc: EpisodeOfCare): string {
  return displayLabelForEpisodeType(eoc.type?.[0]);
}

/** True for in-progress episodes shown elsewhere in the client portal. */
export function isActivePortalEpisode(eoc: EpisodeOfCare): boolean {
  return eoc.status === 'active' || eoc.status === 'onhold';
}

/** Canonical therapy-service-type code from EpisodeOfCare.type (for service-request filtering). */
export function therapyServiceTypeCodeFromEpisode(eoc: EpisodeOfCare): string | undefined {
  return getServiceTypeCode(eoc) ?? codeFromServiceTypeCodeableConcept(eoc.type?.[0]);
}

/** Therapy-service-type codes for the patient's active / on-hold episodes.
 * Omits `group`: Group Therapy requests are scoped by Group Program, not by
 * "already has any Group episode," so the portal still offers Group Therapy. */
export async function fetchActiveEpisodeTherapyTypeCodes(
  medplum: MedplumClient,
  patientId: string,
): Promise<Set<string>> {
  const subject = patientId.startsWith('Patient/') ? patientId : `Patient/${patientId}`;
  try {
    const eocs = await medplum.searchResources('EpisodeOfCare', { patient: subject });
    const codes = new Set<string>();
    for (const eoc of eocs) {
      if (!isActivePortalEpisode(eoc)) continue;
      const code = therapyServiceTypeCodeFromEpisode(eoc);
      if (code && code !== 'group' && code !== 'group-therapy') codes.add(code);
    }
    return codes;
  } catch {
    return new Set();
  }
}

function mapEocToOption(
  e: EpisodeOfCare,
  practitionerLabels: Map<string, string>,
): PortalEocOption | null {
  if (!e.careManager?.reference && !e.careManager?.display) return null;
  const providerId = practitionerIdFromRef(e.careManager?.reference) || (e.careManager?.display ?? 'unknown');
  return {
    eocId: e.id ?? `eoc-${practitionerIdFromRef(e.careManager?.reference)}-${eocDisplayName(e)}`,
    providerId,
    providerName: preferredLabelOrFallback(
      practitionerIdFromRef(e.careManager?.reference),
      practitionerLabels,
      e.careManager?.display,
      'Your provider',
    ),
    providerPreferredName: practitionerLabels.get(
      practitionerIdFromRef(e.careManager?.reference),
    ),
    episodeLabel: eocDisplayName(e),
    status: e.status ?? 'active',
  };
}

/**
 * Active EpisodeOfCare rows for the portal patient.
 * Returns an empty array when none are found or the search fails.
 */
export async function fetchPortalEocOptions(
  medplum: MedplumClient,
  patientId: string
): Promise<PortalEocOption[]> {
  try {
    const eocs = await medplum.searchResources('EpisodeOfCare', { patient: `Patient/${patientId}` });
    const active = eocs.filter(isActivePortalEpisode);
    const practitionerLabels = await loadPractitionerPreferredLabelMap(
      medplum,
      active.map((e) => practitionerIdFromRef(e.careManager?.reference)).filter(Boolean),
    );
    return active
      .map((e) => mapEocToOption(e, practitionerLabels))
      .filter((x): x is PortalEocOption => x !== null);
  } catch {
    return [];
  }
}

/**
 * Resolve the EoC option for an appointment: explicit id first, then practitioner match.
 */
export function matchEocOptionForAppointment(
  options: PortalEocOption[],
  params: {
    episodeOfCareId?: string;
    providerId?: string;
    providerLabel?: string;
  }
): PortalEocOption | null {
  if (params.episodeOfCareId) {
    const byId = options.find((o) => o.eocId === params.episodeOfCareId);
    if (byId) return byId;
  }
  if (params.providerId) {
    const byPractitioner = options.find((o) => o.providerId === params.providerId);
    if (byPractitioner) return byPractitioner;
  }
  const label = params.providerLabel?.trim();
  if (label) {
    const byName = options.find(
      (o) => o.providerName.trim().toLowerCase() === label.toLowerCase()
    );
    if (byName) return byName;
  }
  return null;
}
